import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaMinusSquare, FaPlusSquare } from "react-icons/fa";
import { BiSort } from "react-icons/bi";
import { IoMdLock } from "react-icons/io";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FiDownload } from "react-icons/fi";
import styles from "../commanstyles.module.css";

// Utility function to format currency
const formatCurrency = (value) => {
    if (!value) return "0";
    return new Intl.NumberFormat("en-IN", {
        style: "decimal",
        maximumFractionDigits: 2,
    }).format(value);
};

// Utility function to format date
const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

function SecuritiesAllotmenttable({ data, isSubScribe }) {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [isTableOneOpen, setIsTableOneOpen] = useState(true);
    const [selectedUnit, setSelectedUnit] = useState("Crore");
    const [selectedAllotmentType, setSelectedAllotmentType] = useState("All");
    const [uniqueAllotmentTypes, setUniqueAllotmentTypes] = useState([]);
    const [sortedData, setSortedData] = useState([]);
    const [sorting, setSorting] = useState({
        column: "name", // Default column to sort by
        direction: "asc", // Default sorting direction (ascending)
    });

    const rowsPerPage = 10;

    const toggleTableOne = () => {
        if (!isSubScribe) {
            alert("Please unlock to see the data.");
            return;
        }
        setIsTableOneOpen((prev) => !prev);
    };

    // Build unique list of allotment types for dropdown
    useEffect(() => {
        const types = Array.from(
            new Set(
                (data || [])
                    .map((item) => item?.allotmentType)
                    .filter(Boolean)
            )
        ).sort();
        setUniqueAllotmentTypes(types);
    }, [data]);

    // Apply filter by allotment type
    const filteredData =
        selectedAllotmentType === "All"
            ? data
            : data.filter(
                (row) => row.allotmentType === selectedAllotmentType
            );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const totalItems = filteredData.length;

    const handleClick = (page) => {
        setCurrentPage(page);
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    const convertValue = (value, unit) => {
        if (!value || value === 0) return value;

        switch (unit) {
            case "Crore":
                return value / 1e7; // Converting to Crore (1 Crore = 10,000,000)
            case "Lakh":
                return value / 1e5; // Converting to Lakh (1 Lakh = 100,000)
            case "Million":
                return value / 1e6; // Converting to Million (1 Million = 1,000,000)
            case "INR":
                return value; // INR is the default unit, no conversion
            default:
                return value;
        }
    };

    // Handle sorting change
    const handleSort = (column) => {
        if (sorting.column === column) {
            // Toggle sorting direction
            setSorting({
                column,
                direction: sorting.direction === "asc" ? "desc" : "asc",
            });
        } else {
            setSorting({ column, direction: "asc" });
        }
    };

    // Sorting function
    const sortData = (data) => {
        const { column, direction } = sorting;
        return data.sort((a, b) => {
            if (a[column] < b[column]) {
                return direction === "asc" ? -1 : 1;
            }
            if (a[column] > b[column]) {
                return direction === "asc" ? 1 : -1;
            }
            return 0;
        });
    };

    useEffect(() => {
        setSortedData(sortData(currentData)); // Update sorted data whenever currentData or sorting changes
    }, [currentData, sorting]); // Dependencies: currentData and sorting

    return (
        <div className={styles.table_subcontainer}>
            {isSubScribe && (
                <div className={styles.highlet_header_content1}>
                    <p className={styles.minuse_icon} onClick={toggleTableOne}>
                        {isTableOneOpen ? (
                            <FaMinusSquare size={22} />
                        ) : (
                            <FaPlusSquare size={22} />
                        )}
                    </p>
                    <h3 className={styles.highlet_text}>
                        SECURITIES ALLOTMENT TABLE
                    </h3>

                    {/* Dropdown for unit selection */}
                    {data.length > 0 && (
                        <select
                            value={selectedUnit}
                            onChange={(e) => setSelectedUnit(e.target.value)}
                            className={styles.unitDropdown}>
                            <option value="Crore">Crore</option>
                            <option value="Lakh">Lakh</option>
                            <option value="INR">INR</option>
                            <option value="Million">Million</option>
                        </select>
                    )}

                    {/* Filter by Allotment Type */}
                    {uniqueAllotmentTypes.length > 0 && (
                        <select
                            value={selectedAllotmentType}
                            onChange={(e) => {
                                setSelectedAllotmentType(e.target.value);
                                setCurrentPage(1);
                            }}
                            className={styles.unitDropdown}
                            style={{ marginLeft: 8 }}
                        >
                            <option value="All">All Allotment Types</option>
                            {uniqueAllotmentTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {!isSubScribe && (
                <button className={styles.Subscription_msg} onClick={() => navigate("/kohere/mycredits", { state: { active: "BuyCredits" } })}>
                    <IoMdLock /> Subscription required to view data
                </button>
            )}

            {isSubScribe && isTableOneOpen && (
                <>
                    <table className={styles.commantable}>
                        <thead>
                            <tr className={styles.tableHeaderRow}>
                                <th className={styles.tableHeader}>S.No</th>
                                {/* <th className={styles.tableHeader}>
                  Name
                  <BiSort
                    onClick={() => handleSort("instrument")}
                    style={{ cursor: "pointer" }}
                  />
                </th> */}
                                <th className={styles.tableHeader}>
                                    Allotment Date
                                    <BiSort
                                        onClick={() =>
                                            handleSort("allotmentDate")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    Allotment Mode
                                    <BiSort
                                        onClick={() =>
                                            handleSort("allotmentType")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    Allotment Type
                                    {/* <BiSort
                    onClick={() => handleSort("allotmentDate")}
                    style={{ cursor: "pointer" }}
                  /> */}
                                </th>

                                <th className={styles.tableHeader}>
                                    Amount <br />
                                    ₹({selectedUnit})
                                    <BiSort
                                        onClick={() => handleSort("amount")}
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    No. of Securities Allotted
                                    <BiSort
                                        onClick={() =>
                                            handleSort("number_of_securities_allotted")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    No. of Securities
                                    <BiSort
                                        onClick={() =>
                                            handleSort("no_of_securities")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    Nominal Value <br /> ₹({selectedUnit})
                                    <BiSort
                                        onClick={() =>
                                            handleSort("nominal_value")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    Premium Value <br /> ₹({selectedUnit})
                                    <BiSort
                                        onClick={() =>
                                            handleSort("premium_value")
                                        }
                                        style={{ cursor: "pointer" }}
                                    />
                                </th>
                                <th className={styles.tableHeader}>
                                    Reference Document(s)
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.length > 0 ? (
                                sortedData.map((row, index) => (
                                    <tr
                                        key={row.Id}
                                        className={styles.tableRow}>
                                        <td className={styles.tableCell}>
                                            {index + 1}
                                        </td>

                                        <td className={styles.tableCell}>
                                            {formatDate(row.allotmentDate)}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {row.mode_of_allotment || "-"}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {row.allotmentType || "-"}
                                        </td>
                                        {/* <td className={styles.tableCell}>
                                            {row.type_of_security || "-"}
                                        </td> */}
                                        <td className={styles.tableCell}>
                                            {formatCurrency(
                                                convertValue(
                                                    row.amount,
                                                    selectedUnit
                                                )
                                            )}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {row.number_of_securities_allotted || "-"}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {row.no_of_securities || "-"}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {formatCurrency(
                                                convertValue(
                                                    row.nominal_value,
                                                    selectedUnit
                                                )
                                            )}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {formatCurrency(
                                                convertValue(
                                                    row.premium_value,
                                                    selectedUnit
                                                )
                                            )}
                                        </td>
                                        <td className={styles.tableCell}>
                                            {row.refDoc ? (
                                                <div
                                                    className={
                                                        styles.referencedoc_container_col
                                                    }>
                                                    {row.refDoc
                                                        .MaiinFileName && (
                                                            <a
                                                                href={
                                                                    row.refDoc
                                                                        .fileurl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={
                                                                    styles.referencedoc_mainfilename
                                                                }
                                                                style={{
                                                                    color: "#083A7F",
                                                                    display: "inline-block",
                                                                    marginBottom: "10px",
                                                                    cursor: "pointer",
                                                                }}>
                                                                {
                                                                    row.refDoc
                                                                        .MaiinFileName
                                                                }
                                                            </a>
                                                        )}
                                                    <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
                                                        {row.refDoc
                                                            .Attachments &&
                                                            row.refDoc.Attachments
                                                                .length > 0 ? (
                                                            row.refDoc.Attachments.map(
                                                                (
                                                                    attachment,
                                                                    index
                                                                ) => (
                                                                    <li
                                                                        key={
                                                                            index
                                                                        }
                                                                        className={
                                                                            styles.referencedoc_Attachments
                                                                        }
                                                                        style={{
                                                                            marginBottom: "8px",
                                                                            cursor: "pointer",
                                                                        }}>
                                                                        <IoDocumentTextOutline
                                                                            size={
                                                                                16
                                                                            }
                                                                            style={{
                                                                                marginRight:
                                                                                    "8px",
                                                                                color: "#2961EF",
                                                                                flexShrink: 0,
                                                                                cursor: "pointer",
                                                                            }}
                                                                        />
                                                                        <a
                                                                            href={
                                                                                attachment.fileurl
                                                                            }
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                textDecoration:
                                                                                    "none",
                                                                                color: "#2961EF",
                                                                                cursor: "pointer",
                                                                            }}>
                                                                            {
                                                                                attachment.Name
                                                                            }
                                                                        </a>
                                                                        <button
                                                                            onClick={() => {
                                                                                const link =
                                                                                    document.createElement(
                                                                                        "a"
                                                                                    );
                                                                                link.href =
                                                                                    attachment.fileurl;
                                                                                link.download =
                                                                                    attachment.Name;
                                                                                link.click(); // Trigger the download
                                                                            }}
                                                                            title="Download"
                                                                            style={{
                                                                                background:
                                                                                    "none",
                                                                                border: "none",
                                                                                cursor: "pointer",
                                                                                color: "#F5B301",
                                                                                marginLeft:
                                                                                    "10px",
                                                                                textDecoration:
                                                                                    "underline",
                                                                            }}>
                                                                            <FiDownload />
                                                                        </button>
                                                                    </li>
                                                                )
                                                            )
                                                        ) : (
                                                            <span>
                                                                No attachments
                                                                available.
                                                            </span>
                                                        )}
                                                    </ul>
                                                </div>
                                            ) : (
                                                <span>
                                                    No documents available for
                                                    selected year
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="10"
                                        className={styles.noDataRow} style={{ textAlign: "center" }}>
                                        No Security allotment data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className={styles.pagination}>
                        <button
                            onClick={handlePrevious}
                            disabled={currentPage === 1}>
                            &lt;
                        </button>
                        {[...Array(totalPages).keys()].map((num) => (
                            <button
                                key={num + 1}
                                onClick={() => handleClick(num + 1)}
                                className={
                                    currentPage === num + 1 ? styles.active : ""
                                }>
                                {num + 1}
                            </button>
                        ))}
                        <button
                            onClick={handleNext}
                            disabled={currentPage === totalPages}>
                            &gt;
                        </button>
                    </div>

                    {/* Display total items and current page */}
                    <div className={styles.pageInfo}>
                        <p>
                            Showing {indexOfFirstRow + 1} to{" "}
                            {Math.min(indexOfLastRow, totalItems)} of{" "}
                            {totalItems} entries
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

export default SecuritiesAllotmenttable;
